import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

const Section = styled.section`
  padding: 120px 2rem;
  background: #0D0D0D;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 80px 1.25rem; }
`;

const SideImg = styled(motion.img)`
  position: absolute;
  width: 200px;
  border-radius: 14px;
  object-fit: cover;
  box-shadow: 0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08);
  pointer-events: none;
  z-index: 0;

  @media (max-width: 1100px) { width: 150px; }
  @media (max-width: 768px)  { width: 100px; }
`;

const BgAccent = styled.div`
  position: absolute;
  right: -200px;
  top: 50%;
  transform: translateY(-50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(244,121,32,0.07) 0%, transparent 65%);
  pointer-events: none;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  margin-bottom: 5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 3rem;
    text-align: center;
  }
`;

const EyebrowWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) { justify-content: center; }
`;

const Line = styled.div`
  width: 32px;
  height: 2px;
  background: #F47920;
  border-radius: 1px;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 600;
`;

const Title = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(2.4rem, 4.5vw, 3.8rem);
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1.0;
`;

const Accent = styled.span`
  color: #F47920;
`;

const HeaderRight = styled.p`
  font-size: 0.95rem;
  color: #fff;
  line-height: 1.75;
  max-width: 400px;
  align-self: end;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0 auto;
  }
`;

const StepsWrap = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
  background: linear-gradient(160deg, #161616 0%, #111111 100%);
  border: 1px solid #1E1E1E;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(244,121,32,0.08);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    border-radius: 14px;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const ConnectorLine = styled.div`
  position: absolute;
  top: 52px;
  left: calc(10% + 24px);
  right: calc(10% + 24px);
  height: 1px;
  background: linear-gradient(90deg, #F47920, rgba(244,121,32,0.4), rgba(244,121,32,0.1));
  z-index: 0;

  @media (max-width: 1024px) { display: none; }
`;

const ProgressLine = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #F47920;
  border-radius: 1px;
  transform-origin: left;
`;

const Step = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
  padding: 1.75rem 1rem 1.5rem;

  @media (max-width: 1024px) {
    flex-direction: row;
    text-align: left;
    padding: 1.25rem 1.25rem;
    gap: 1.5rem;
    border-bottom: 1px solid #1E1E1E;
    &:last-child { border-bottom: none; }
  }
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ $active }) => $active ? '#F47920' : '#161616'};
  border: 1px solid ${({ $active }) => $active ? '#F47920' : '#2A2A2A'};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => $active ? '#fff' : '#F47920'};
  margin-bottom: 1.5rem;
  flex-shrink: 0;
  transition: background 0.3s ease, border-color 0.3s ease;

  svg { width: 26px; height: 26px; }

  @media (max-width: 1024px) {
    margin-bottom: 0;
    width: 52px;
    height: 52px;
  }
`;

const StepNum = styled.div`
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 700;
  margin-bottom: 0.4rem;
`;

const StepTitle = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 1.25rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 0.5rem;
`;

const StepDesc = styled.div`
  font-size: 0.8rem;
  color: #fff;
  line-height: 1.6;
  max-width: 160px;

  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const STEPS = [
  {
    num: '01',
    title: 'Engorda',
    desc: 'Crianza y engorda de ganado propio bajo estrictos estándares nutritivos.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    ),
    active: true,
  },
  {
    num: '02',
    title: 'Sacrificio',
    desc: 'Proceso bajo las más estrictas normas de higiene y bienestar animal.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    ),
    active: false,
  },
  {
    num: '03',
    title: 'Industrialización',
    desc: 'Corte, empaque y procesamiento con tecnología moderna.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
    active: false,
  },
  {
    num: '04',
    title: 'Comercialización',
    desc: 'Venta directa al mayoreo para negocios y distribuidores.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
      </svg>
    ),
    active: false,
  },
  {
    num: '05',
    title: 'Distribución',
    desc: 'Cobertura estratégica a nivel local y nacional en todo México.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM21 17a2 2 0 11-4 0 2 2 0 014 0z"/>
        <path d="M3 6h11l1.68 8.39a2 2 0 001.978 1.61H21V9a3 3 0 00-3-3h-1M3 6l-1 5h10M3 6H1"/>
      </svg>
    ),
    active: false,
  },
];

export default function Process() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const yLeft  = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const yRight = useTransform(scrollYProgress, [0, 1], [-90, 90]);

  return (
    <Section id="proceso" ref={ref}>
      <BgAccent />
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (22).webp"
        alt=""
        style={{ left: '-70px', bottom: '0px', rotate: '-10deg', y: yLeft }}
      />
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (24).webp"
        alt=""
        style={{ right: '-40px', top: '28%', rotate: '10deg', y: yRight }}
      />
      <Inner>
        <Header>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <EyebrowWrap>
              <Line />
              <Eyebrow>Cadena de Valor</Eyebrow>
            </EyebrowWrap>
            <Title>
              Control <Accent>Total</Accent><br />
              Del Proceso
            </Title>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <HeaderRight>
              Somos una empresa integrada verticalmente. Controlamos con orgullo cada
              etapa de la cadena de valor, garantizando la calidad en cada paso.
            </HeaderRight>
          </motion.div>
        </Header>

        <StepsWrap>
          <ConnectorLine>
            <ProgressLine
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              transition={{ duration: 1.4, delay: 0.4, ease: 'easeOut' }}
            />
          </ConnectorLine>

          {STEPS.map((step, i) => (
            <Step
              key={step.num}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.3 + i * 0.1 }}
            >
              <IconWrap $active={step.active}>
                {step.icon}
              </IconWrap>
              <div>
                <StepNum>Paso {step.num}</StepNum>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.desc}</StepDesc>
              </div>
            </Step>
          ))}
        </StepsWrap>
      </Inner>
    </Section>
  );
}
